/* eslint-disable func-names */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import DeleteIcon from "@mui/icons-material/Delete";
import { Icon } from "@iconify/react";
import _ from "lodash";
import { toast } from "react-toastify";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Dialog } from "@mui/material";
import {
  Box,
  ButtonPrimary,
  ButtonPrimary1,
  formatToTitleCase,
  Error,
  HStack,
  IconButtonType,
  IconCTAButton,
  VStack,
  getGrantStrikePrice,
} from "../../components/utils";
import {
  GrantedBy,
  useGrantDetailsStore,
} from "../../store/useGrantDetailsStore";
import { formatDisplayDate } from "../../utils/date";
import FileInput from "../../components/shared/FileInput";
import { Label, TextArea } from "../../components/shared/InputField";
import convertToBase64 from "../../utils/convertToBase64";
import { SwitchButton } from "../../components/shared/SwitchButton";
import Tooltip from "../../components/shared/Tooltip";
import {
  useAddGrant,
  useEditGrant,
  useGetEditGrantDetails,
  useGetGrantDetailsForValidation,
  useGrants,
} from "../../queries/esopPlan";
import { AddGrantReq, VestingDateType } from "../../types/Grant";
import { useAcceptRecommendedChanges } from "../../queries/grantApproval";
import LazyPdfDocument from "../../components/shared/LazyPdfDocument";
import { useGetAllMilestones } from "../../queries/milestone";
import { VestingMilestone } from "../../types/milestone";
import {
  formatCurrency,
  getCurrencySymbol,
  getCurrencyType,
} from "../../utils/currencyFormatter";
import { StrikePriceType } from "../../types/EsopPlan";
import InfoBox from "../../components/shared/InfoBox";
import { Select } from "../../components/shared/Select";
import {
  useGetGrantLetterSettings,
  useGetGrantLetterTemplates,
} from "../../queries/siteSettings";
import { useGetAllMatrices } from "../../queries/allocationMatrix";
import { useRegionalGrantRules } from "../../queries/onBoarding";
import { useCompanyStore } from "../../store";
import { resolveRegionalGrantRule } from "../../utils/regionalGrantRules";
import { GrantLetterTemplate } from "../../types/SiteSettings";
import {
  areValidStandardTags,
  isValidStandardInput,
  getServerErrorMessage,
  getServerValidationErrors,
  standardInputMessage,
} from "../../utils/standardInputValidation";

interface GrantSummaryPageProps {
  onStepChange: (e: number) => void;
  onBackClick: () => void;
}

function parseNoteEntries(
  note: string
): { state: string; author: string; date: string; text: string }[] {
  if (!note) return [];
  try {
    const parsed = JSON.parse(note);
    if (Array.isArray(parsed)) return parsed;
  } catch (_) {
    // not JSON — return empty
  }
  return [];
}

const GrantSummaryPage = (props: GrantSummaryPageProps) => {
  const currencyType = getCurrencyType();
  const currencySymbol = getCurrencySymbol();
  const data = useGrantDetailsStore();
  const { companyData } = useCompanyStore();
  const { data: regionalRuleCatalog } = useRegionalGrantRules();
  const regionalRule = resolveRegionalGrantRule(
    regionalRuleCatalog?.rules,
    companyData?.companyMetaData?.registeredCountry
  );
  const isStrikePriceOptional =
    data.plan?.planType === "ESOP" &&
    regionalRule.strikePriceOptional !== undefined;
  const navigate = useNavigate();
  const location = useLocation();
  const { mode, id } = useParams();
  const recommendationContext = location.state?.recommendationContext;
  const { mutate: createGrant, isLoading } = useAddGrant();
  const { mutate: editGrant, isLoading: editLoading } = useEditGrant();
  const { mutateAsync: resolveRecommendation } = useAcceptRecommendedChanges();
  const { data: _grants } = useGetGrantDetailsForValidation({
    identifier: true,
    dateOfGrant: true,
  });
  const [errors, setErrors] = useState<any>({});
  const [newNote, setNewNote] = useState("");
  const { data: _allMilestones, isFetched } = useGetAllMilestones();
  const [allMilestones, setAllMilestones] = useState<VestingMilestone[]>([]);
  const oldData = useGetEditGrantDetails(id || "");
  const { data: templates } = useGetGrantLetterTemplates();
  const { data: grantSettings } = useGetGrantLetterSettings();
  const generateGrantLetter = !data.uploadCustomGrantLetter;
  const useAllocationMatrix =
    grantSettings?.useAllocationMatrixForGranting ?? false;
  const { data: matrices } = useGetAllMatrices();
  const matrixName =
    (matrices ?? []).find((m: any) => m.id === data.allocationMatrixId)?.name ||
    data.allocationMatrixId ||
    "";
  const grantLetterTemplates = useMemo(() => {
    const list: GrantLetterTemplate[] = Array.isArray(templates)
      ? templates
      : (templates as any)?.grantLetterTemplates ??
        (templates as any)?.data ??
        [];
    return list.filter((t) => Boolean(t?.entityType === "GRANT"));
  }, [templates]);
  const hasGrantLetterTemplate = Boolean(data.grantLetterTemplate);
  const hasCustomGrantLetter = Boolean(
    data.customizedGrantLetter?.file || data.customizedGrantLetter?.link
  );
  const [regenerateGrantLetter, setRegenerateGrantLetter] = useState(false);
  const regenerateInitialized = useRef(false);
  useEffect(() => {
    setAllMilestones(_allMilestones || []);
  }, [isFetched]);
  const [expanded, setExpanded] = useState<{
    newGrant: boolean;
    grantInformation: boolean;
    vestingSchedule: boolean;
  }>({ newGrant: true, grantInformation: false, vestingSchedule: false });
  const [documentDialog, setDocumentDialog] = useState<{
    open: boolean;
    grantLetter?: string;
  }>({ open: false, grantLetter: undefined });
  async function handleFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    if (!e?.target?.files?.[0]) return;
    const fileData = e.target.files[0];
    if (!fileData.name.toLowerCase().endsWith(".pdf")) return;
    const base64 = await convertToBase64(fileData);
    data.setCustomizedGrantLetter({
      file: base64 as unknown as string,
      fileName: fileData.name,
      fileType: "pdf",
      customizedLetter: true,
    });
    e.target.value = "";
    setErrors((current: any) => ({ ...current, customGrantLetter: undefined }));
  }
  async function handleTemplateFileUpload(
    e: React.ChangeEvent<HTMLInputElement>
  ) {
    if (!e?.target?.files?.[0]) return;
    const fileData = e.target.files[0];
    if (!fileData.name.toLowerCase().endsWith(".docx")) return;
    const base64 = await convertToBase64(fileData);
    data.setGrantLetterTemplate({
      templateName: fileData.name,
      grantLevelTemplate: base64 as string,
      planName: "",
      isAdded: true,
      isChanged: false,
      isDeleted: false,
      isPrivate: false,
    });
    e.target.value = "";
  }
  function handleDeleteGrantLetterTemplate() {
    data.setGrantLetterTemplate(undefined);
  }
  const grants = useMemo(() => _grants || [], [_grants]);
  function handleDeleteLetter() {
    data.setCustomizedGrantLetter({
      file: "",
      fileName: "",
      fileType: "",
      customizedLetter: false,
    });
  }
  const checkIfGrantIdExists = (value: string, mode = "add", id = "") => {
    if (grants) {
      const filteredGrants =
        mode === "edit" ? grants.filter((g) => g.id !== id) : grants;
      return filteredGrants.some((grant) => grant.identifier === value);
    } else return false;
  };

  const shouldRegenerateGrantLetter = useMemo(() => {
    const old = oldData.data;
    if (!old) return false;
    if (!data || !data.employee?.id || !data.plan?.esopPlanId) return false;

    return (
      (old.dateOfGrant && data.dateOfGrant
        ? new Date(old.dateOfGrant).setHours(0, 0, 0, 0) !==
          new Date(data.dateOfGrant).setHours(0, 0, 0, 0)
        : old.dateOfGrant !== data.dateOfGrant) ||
      (old.vestingDate && data.vestingDate
        ? new Date(old.vestingDate).setHours(0, 0, 0, 0) !==
          new Date(data.vestingDate).setHours(0, 0, 0, 0)
        : old.vestingDate !== data.vestingDate) ||
      Number(old.grantPrice) !== Number(data.grantPrice) ||
      Number(old.optionsGranted) !== Number(data.optionsGranted) ||
      (old.vestingDateType || "") !== (data.vestingDateType || "") ||
      (old.vestingTemplate?.id || "") !== (data.vestingTemplate?.id || "") ||
      (old.actualVestingDay || "") !== (data.actualVestingDay || "") ||
      (old.plan?.planName || "") !== (data.plan?.planName || "") ||
      Boolean(old.customizedGrantLetter?.customizedLetter) !==
        Boolean(data.uploadCustomGrantLetter) ||
      (old.grantLetterTemplate?.templateId || "") !==
        (data.grantLetterTemplate?.templateId || "") ||
      Boolean(data.grantLetterTemplate?.isAdded)
    );
  }, [
    oldData.data?.dateOfGrant,
    oldData.data?.grantPrice,
    oldData.data?.optionsGranted,
    oldData.data?.vestingDateType,
    oldData.data?.vestingDate,
    oldData.data?.vestingTemplate?.id,
    oldData.data?.actualVestingDay,
    oldData.data?.plan?.planName,
    oldData.data?.grantLetterTemplate?.templateId,
    oldData.data?.customizedGrantLetter?.customizedLetter,
    data.uploadCustomGrantLetter,
    data.dateOfGrant,
    data.grantPrice,
    data.optionsGranted,
    data.vestingDateType,
    data.vestingDate,
    data.vestingTemplate?.id,
    data.actualVestingDay,
    data.plan?.planName,
    data.employee?.id,
    data.plan?.esopPlanId,
    data.grantLetterTemplate?.templateId,
    data.grantLetterTemplate?.isAdded,
  ]);
  const replacingCustomLetter =
    mode === "edit" &&
    Boolean(oldData.data?.customizedGrantLetter?.customizedLetter) &&
    !data.uploadCustomGrantLetter;

  useEffect(() => {
    if (mode !== "edit") return;
    if (!regenerateInitialized.current) {
      setRegenerateGrantLetter(shouldRegenerateGrantLetter);
      regenerateInitialized.current = true;
      return;
    }
    setRegenerateGrantLetter(shouldRegenerateGrantLetter);
  }, [shouldRegenerateGrantLetter, mode]);
  const validationSchema = Yup.object().shape({
    employee: Yup.object().required("Name of Holder is required"),
    plan: Yup.object().required("Plan Name is required"),
    dateOfGrant: Yup.date()
      .required("Grant Date is required")
      .when("employee", (employee, schema) =>
        schema.test(
          "is-after-joining-and-plan-start",
          `Grant Date must be after the employee's joining date and the plan start date.`,
          // eslint-disable-next-line func-names
          function (value) {
            const { plan } = this.parent as {
              plan?: { planStartDate?: Date; startDate?: Date };
            };
            const employeeJoiningDate = employee?.dateOfJoin;

            const startDate = plan?.planStartDate || plan?.startDate;

            if (!value || !employeeJoiningDate || !startDate) return true;

            const grantDate = new Date(value).setHours(0, 0, 0, 0);
            const joiningDate = new Date(employeeJoiningDate).setHours(
              0,
              0,
              0,
              0
            );
            const planStartDate = new Date(startDate).setHours(0, 0, 0, 0);

            return grantDate >= joiningDate && grantDate >= planStartDate;
          } as (this: Yup.TestContext, value: Date) => boolean
        )
      ),
    grantPrice: Yup.number()

      .test(
        "grant-price-required",
        "Grant Price is required",
        (value) =>
          isStrikePriceOptional || (value !== undefined && value !== null)
      )
      // .min(0, "Grant Price cannot be lesser than zero")
      .test(
        "regional-strike-price-required",
        `Grant Price is required for ${regionalRule.country}`,
        (value) => {
          if (value === undefined || value === null) return true;
          if (data.plan?.planType === "RSU") return value >= 0.0001;
          if (data.plan?.planType !== "ESOP") return value >= 0;
          return isStrikePriceOptional || value > 0;
        }
      )
      .test(
        "regional-grant-price-minimum",
        `Grant Price must be at least ${regionalRule.minStrikePrice}`,
        (value) =>
          value === undefined ||
          value === null ||
          data.plan?.planType !== "ESOP" ||
          isStrikePriceOptional ||
          regionalRule.minStrikePrice === undefined ||
          value >= regionalRule.minStrikePrice
      ),
    grantId: Yup.string()
      .test("safe-grant-id", standardInputMessage("Grant ID"), (value) =>
        isValidStandardInput(value)
      )
      .test("unique-grant-id", "This Grant ID already exists.", (value) => {
        if (!value) return true;
        const exists = checkIfGrantIdExists(value, mode, id);
        return !exists;
      }),
    note: Yup.string().test(
      "safe-note",
      standardInputMessage("Note"),
      (value) => isValidStandardInput(value)
    ),
    grantType: Yup.string().test(
      "safe-grant-type",
      standardInputMessage("Grant type"),
      (value) => isValidStandardInput(value)
    ),
    tags: Yup.mixed().test("safe-tags", standardInputMessage("Tags"), (value) =>
      areValidStandardTags(value)
    ),
    remarks: Yup.string().test(
      "safe-remarks",
      standardInputMessage("Remarks"),
      (value) => isValidStandardInput(value)
    ),
    templateName: Yup.string().test(
      "safe-template-name",
      standardInputMessage("Template name"),
      (value) => isValidStandardInput(value)
    ),
    grantedBy: Yup.string().required("Select Granted By"),
    noOfOptions: Yup.number()
      .required("No of Options is required")
      .min(
        data?.plan?.isFractional ? 0.0001 : 1,
        `No of Options must be greater than ${
          data?.plan?.isFractional ? 0.0001 : 1
        }`
      )
      .max(
        (data?.plan?.optionsReserved || 0) + (data?.oldOptionsGranted || 0),
        "No. of Options must be lesser than the Options Available"
      ),
    modellingId: Yup.string().when("grantedBy", {
      is: "MODEL",
      then: Yup.string().required("Model is Required"),
      otherwise: Yup.string().notRequired(),
    }),

    selectedExistingTemplate: Yup.boolean().required(
      "No of Options is required"
    ),
    selectedVestingTemplate: Yup.string().when("selectedExistingTemplate", {
      is: true,
      then: Yup.string().required(
        "Vesting Template is required when selecting an existing template"
      ),
    }),
    vestingScheduleData: Yup.array()
      .when("selectedExistingTemplate", {
        is: false,
        then: Yup.array()
          .of(
            Yup.object().shape({
              vestingDate: Yup.string().required("Vesting Date is required"),
              percentage: Yup.number().required("Percentage is required"),
              vestingOptions: Yup.number().required(
                "Vesting Options are required"
              ),
              milestoneId: Yup.string().optional(),
            })
          )
          .required("Vesting Schedule Data is required")
          .test(
            "options-match",
            "Sum of Vesting Options must match Options Granted",
            function (vestingScheduleData) {
              const { optionsGranted, isFractional } = this.parent || {};

              if (!vestingScheduleData || !optionsGranted) return true;
              const totalVestingOptions = vestingScheduleData.reduce(
                (sum, item) => sum + (item.vestingOptions || 0),
                0
              );

              return isFractional
                ? parseFloat(totalVestingOptions.toFixed(4)) === optionsGranted
                : parseInt(totalVestingOptions.toString(), 10) ===
                    optionsGranted;
            }
          ),
      })
      .test(
        "vesting-date-check",
        "Vesting Date cannot be earlier than the Grant Date",
        function (vestingScheduleData) {
          const { dateOfGrant } = this.parent || {};

          if (!vestingScheduleData || !dateOfGrant) return true;

          const isValid = vestingScheduleData.every((item) => {
            const vestingDate = new Date(item?.vestingDate || new Date());
            const grantDateObj = new Date(dateOfGrant);

            return vestingDate >= grantDateObj;
          });

          return isValid;
        }
      ),
  });

  const formik = useFormik({
    initialValues: {},
    validationSchema,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: () => {},
  });

  const validateData = async () => {
    const errors = await formik.validateForm({
      selectedExistingTemplate: data.selectedExistingTemplate,
      selectedVestingTemplate: data?.vestingTemplate?.id || "",
      optionsGranted: data?.optionsGranted,
      vestingScheduleData: data?.vestingScheduleData,
      noOfOptions: data.optionsGranted || 0,
      modellingId: data.modellingId,
      employee: data.employee,
      grantId: data.grantId,
      note: newNote,
      grantType: data.grantType,
      tags: data.tags,
      remarks: data.remarks,
      templateName: data.manualTemplateName,
      plan: data.plan,
      dateOfGrant: data.dateOfGrant,
      grantPrice: data.grantPrice || 0,
      grantedBy: data.grantedBy || GrantedBy.OPTIONS,
      isFractional: data?.plan?.isFractional || false,
    });
    return errors;
  };

  function handleSubmit() {
    validateData().then((errors) => {
      if (_.isEmpty(errors)) {
        if (data.employee?.id && data.plan?.esopPlanId) {
          const addReqData: AddGrantReq = {
            employeeId: data.employee.id,
            employeeName: data.employee.employeeName,
            planId: data.plan.esopPlanId,
            planName: data.plan.planName,
            planType: data.plan.planType,
            optionsGranted: data.optionsGranted,
            grantPrice: data.grantPrice || 0,
            grantType:
              data.grantType && data.grantType !== ""
                ? data.grantType
                : undefined,
            dateOfGrant: data.dateOfGrant,
            vestingDateType: data.selectedExistingTemplate
              ? data.vestingDateType
              : VestingDateType.GRANT_DATE,
            vestingTemplate: data.selectedExistingTemplate
              ? data.vestingTemplate.vestingTemplateName
              : "",
            vestingId: data.selectedExistingTemplate
              ? data.vestingTemplate.id
              : "",
            vestingDate: data.selectedExistingTemplate ? data.vestingDate : "",
            generateGrantLetter,
            customizedGrantLetter:
              data.uploadCustomGrantLetter && hasCustomGrantLetter,
            grantLetterType:
              data.uploadCustomGrantLetter && data.customizedGrantLetter?.file
                ? data.customizedGrantLetter?.fileType || "pdf"
                : "",
            grantLetter: data.uploadCustomGrantLetter
              ? data.customizedGrantLetter?.file || ""
              : "",
            grantLetterFileName:
              data.uploadCustomGrantLetter && data.customizedGrantLetter?.file
                ? data.customizedGrantLetter?.fileName || ""
                : "",
            actualVestingDay: data.selectedExistingTemplate
              ? data.actualVestingDay
              : "SAME DAY",
            grantId: data.grantId || "",
            grantedBY: data.grantedBy || GrantedBy.OPTIONS,
            manuallyCreatedTemplate: !data.selectedExistingTemplate,
            templateName: data.selectedExistingTemplate
              ? ""
              : data.manualTemplateName,
            vestingCliff: !data.selectedExistingTemplate
              ? data.manualVestingCliff
              : undefined,
            vestingScheduleData: data.selectedExistingTemplate
              ? []
              : data.vestingScheduleData,
            sharePrice: data.sharePrice,
            valuationId: data.valuationId,
            modellingId: data.modellingId,
            id,
            note: newNote.trim(),
            remarks: data.remarks || undefined,
            tags: data.tags,
            grantLetterTemplate: generateGrantLetter
              ? data.grantLetterTemplate || undefined
              : undefined,
            allocationMatrixId: data.allocationMatrixId || undefined,
            matchedRule: data.matchedRule ?? undefined,
          };
          if (mode === "add") {
            createGrant(addReqData, {
              onSuccess: () => {
                toast("Grant Created successfully!", {
                  type: "success",
                  autoClose: 2000,
                });
                data.clear();
                navigate("/options/allPlans");
              },
              onError: (error: any) => {
                const serverErrors = getServerValidationErrors(error);
                if (!_.isEmpty(serverErrors)) setErrors(serverErrors);
                const errorMessage = getServerErrorMessage(error);
                if (errorMessage.includes("Template Error")) {
                  toast(errorMessage, {
                    type: "warning",
                    autoClose: 5000,
                  });
                  navigate("/options/allPlans");
                } else {
                  toast(errorMessage, {
                    type: "error",
                    autoClose: 5000,
                  });
                }
              },
            });
          } else if (mode === "edit") {
            addReqData.regenerateGrantLetter =
              replacingCustomLetter || regenerateGrantLetter;
            editGrant(addReqData, {
              onSuccess: async () => {
                toast("Grant Edited successfully!", {
                  type: "success",
                  autoClose: 5000,
                });
                if (recommendationContext && id) {
                  try {
                    await resolveRecommendation({
                      grantId: id,
                      signOffId: recommendationContext.signOffId,
                    });
                  } catch {
                    // Recommendation resolution failure should not block the saved grant.
                  }
                }
                data.clear();
                navigate("/options/allPlans");
              },
              onError: (error: any) => {
                const serverErrors = getServerValidationErrors(error);
                if (!_.isEmpty(serverErrors)) setErrors(serverErrors);
                toast(getServerErrorMessage(error), {
                  type: "error",
                  autoClose: 5000,
                });
                if (_.isEmpty(serverErrors)) navigate("/options/allPlans");
              },
            });
          }
        }
      } else {
        setErrors(errors);
        setExpanded({
          grantInformation: true,
          newGrant: true,
          vestingSchedule: true,
        });
      }
    });
  }

  return (
    <VStack className="w-full gap-5 px-4">
      <VStack className="justify-between gap-1 pb-3 pl-2 font-medium border-b-[0.5px] pt-7">
        <HStack className="flex justify-between font-semibold text-lg1 text-black-501 ">
          Grant Summary
        </HStack>
        <HStack className="flex font-medium text-sm3 text-gray-401">
          Review the grant details and vesting schedule, and finalize the ESOP
          grant.
        </HStack>
      </VStack>
      <VStack className="flex-1 border rounded-lg shadow-box">
        <HStack
          className="items-center justify-between p-4 cursor-pointer"
          onClick={() =>
            setExpanded((prev) => ({ ...prev, newGrant: !prev.newGrant }))
          }
        >
          <VStack>
            <h5 className="text-sm font-medium text-black-501">New Grant</h5>
            {!expanded.newGrant && (
              <HStack className="gap-2 pt-1 text-gray-400 text-xxs">
                <span>
                  Employee Name: {data?.employee?.employeeName || ""} |{""}
                </span>
                <span>Plan Name: {data?.plan?.planName || ""}</span>
              </HStack>
            )}
          </VStack>
          <HStack className="items-center gap-8">
            {expanded.newGrant && (
              <ButtonPrimary1
                onClick={(e) => {
                  e.stopPropagation();
                  props.onStepChange(1);
                }}
              >
                Edit
              </ButtonPrimary1>
            )}
            <Box>
              {expanded.newGrant ? (
                <Icon icon="fe:arrow-up" width={16} />
              ) : (
                <Icon icon="fe:arrow-up" width={16} className="rotate-180" />
              )}
            </Box>
          </HStack>
        </HStack>
        {expanded.newGrant && (
          <HStack className="flex-wrap grow">
            {[
              {
                field: "Grant By",
                value: formatToTitleCase(data?.grantedBy || "OPTIONS"),
                error: errors.grantedBy,
              },
              {
                field: "Name of Holder",
                value: data.employee?.employeeName || data.employee?.name || "",
                error: errors.employeeName,
              },
              {
                field: "Grant Id",
                value: !data.grantId
                  ? "It will get Auto-Generated"
                  : data.grantId,
                error: errors.grantId,
              },
              {
                field: "Plan Name",
                value: data.plan?.planName || "",
                error: errors.planName,
              },
              {
                field: "Grant Date",
                value: data.dateOfGrant
                  ? formatDisplayDate(data.dateOfGrant)
                  : "",
                error: errors.dateOfGrant,
              },
              {
                field: "Grant Type",
                value: !data.grantType ? "ESOP" : data.grantType,
                error: errors.grantType,
              },
              ...(data.tags?.length
                ? [
                    {
                      field: "Tags",
                      value: data.tags.map((tag) => tag.label).join(", "),
                      error: errors.tags,
                    },
                  ]
                : []),
              ...(data.allocationMatrixId
                ? [{ field: "Allocation Matrix", value: matrixName }]
                : []),
              ...(data.remarks
                ? [
                    {
                      field: "Remarks",
                      value: data.remarks,
                      error: errors.remarks,
                    },
                  ]
                : []),
            ].map(({ field, value, error }, i) => (
              <HStack key={i} className="w-1/2 gap-1 p-4">
                <span className="w-1/3 text-sm font-normal text-gray-400">
                  {field}
                </span>
                <VStack className="w-2/3">
                  <span className="text-sm font-normal ">
                    {value?.toString()}
                  </span>
                  {error && <Error text={error} />}
                </VStack>
              </HStack>
            ))}
            {data.note && (
              <div className="w-full px-4 pb-4">
                <span className="text-sm font-normal text-gray-400 block mb-2">
                  Note
                </span>
                <div className="flex flex-col gap-2">
                  {(() => {
                    const entries = parseNoteEntries(data.note);
                    const legacy = entries.filter(
                      (e) => !e.state && !e.author && !e.date
                    );
                    const structured = entries.filter(
                      (e) => e.state || e.author || e.date
                    );
                    return (
                      <>
                        {legacy.length > 0 && (
                          <div className="rounded-lg bg-gray-50 border border-borderColor px-3 py-2">
                            <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-light mb-1">
                              Earlier notes
                            </p>
                            {legacy.map((e, i) => (
                              <p
                                key={i}
                                className="text-sm text-secondaryText py-1 border-t border-borderColor first:border-0 first:pt-0"
                              >
                                {e.text}
                              </p>
                            ))}
                          </div>
                        )}
                        {structured.map((e, i) => (
                          <div
                            key={i}
                            className="border border-borderColor rounded-lg p-3 bg-white"
                          >
                            <div className="flex items-center gap-1.5 flex-wrap mb-1">
                              {e.state && (
                                <span className="text-[11px] font-medium px-2 py-0.5 rounded-sm bg-blue-50 text-blue-600">
                                  {e.state.replace(/_/g, " ")}
                                </span>
                              )}
                              {e.author && (
                                <>
                                  <span className="text-xs text-gray-light">
                                    ·
                                  </span>
                                  <span className="text-xs font-medium text-primaryText">
                                    {e.author}
                                  </span>
                                </>
                              )}
                              {e.date && (
                                <>
                                  <span className="text-xs text-gray-light">
                                    ·
                                  </span>
                                  <span className="text-xs text-gray-light">
                                    {e.date}
                                  </span>
                                </>
                              )}
                            </div>
                            <p className="text-sm text-secondaryText">
                              {e.text}
                            </p>
                          </div>
                        ))}
                      </>
                    );
                  })()}
                </div>
              </div>
            )}
          </HStack>
        )}
      </VStack>
      <VStack className="flex-1 border rounded-lg shadow-box">
        <HStack
          className="items-center justify-between p-4 cursor-pointer"
          onClick={() =>
            setExpanded((prev) => ({
              ...prev,
              grantInformation: !prev.grantInformation,
            }))
          }
        >
          <VStack>
            <h5 className="text-sm font-medium text-black-501">
              Grant Information
            </h5>
            {!expanded.grantInformation && (
              <HStack className="gap-2 pt-1 text-gray-400 text-xxs">
                <span>No of Options: {data?.optionsGranted || ""}</span>|{" "}
                {!expanded.grantInformation && (
                  <span>
                    Cost of Grant:{" "}
                    {formatCurrency(
                      data.optionsGranted *
                        getGrantStrikePrice(
                          data?.grantPrice || 0,
                          data.plan?.conversionShares || 1,
                          data.plan?.strikePriceType ||
                            StrikePriceType.OPTIONS_PRICE
                        ) || 0,
                      currencyType
                    )}
                  </span>
                )}
                |{" "}
                {!expanded.grantInformation && (
                  <span>
                    Value At Grant:{" "}
                    {formatCurrency(
                      data.optionsGranted *
                        data.sharePrice *
                        (data.plan?.conversionShares || 1),
                      currencyType
                    ) || ""}
                  </span>
                )}
              </HStack>
            )}
          </VStack>
          <HStack className="items-center gap-8">
            {expanded.grantInformation && (
              <ButtonPrimary1
                onClick={(e) => {
                  e.stopPropagation();
                  props.onStepChange(2);
                }}
              >
                Edit
              </ButtonPrimary1>
            )}
            <Box>
              {expanded.grantInformation ? (
                <Icon icon="fe:arrow-up" width={16} />
              ) : (
                <Icon icon="fe:arrow-up" width={16} className="rotate-180" />
              )}
            </Box>
          </HStack>
        </HStack>
        {expanded.grantInformation && (
          <HStack className="flex-wrap grow">
            {[
              {
                field: "No of Options",
                value: data.optionsGranted.toLocaleString(currencyType, {
                  maximumFractionDigits: 4,
                }),
                error: errors.noOfOptions,
              },
              {
                field: "Share Price",
                value: formatCurrency(data.sharePrice, currencyType),
              },
              {
                field: "Value At Grant",
                value:
                  data.grantedBy === GrantedBy.AMOUNT
                    ? formatCurrency(
                        data.optionsGranted *
                          data.sharePrice *
                          (data.plan?.conversionShares || 1),
                        currencyType
                      )
                    : formatCurrency(data.valueOfOptions, currencyType),
              },
              {
                field: "Cost of Grant",
                value: formatCurrency(
                  data.optionsGranted *
                    getGrantStrikePrice(
                      data?.grantPrice || 0,
                      data.plan?.conversionShares || 1,
                      data.plan?.strikePriceType ||
                        StrikePriceType.OPTIONS_PRICE
                    ),
                  currencyType
                ),
              },
              {
                field: "Grant Price",
                value: formatCurrency(data.grantPrice, currencyType),
                error: errors.grantPrice,
              },
            ].map(({ field, value, error }, i) => (
              <HStack key={i} className="w-1/2 gap-1 p-4">
                <span className="w-1/3 text-sm font-normal text-gray-400">
                  <HStack className="gap-1">
                    {field}
                    {field === "Value At Grant" && (
                      <Tooltip
                        text={`Calculated as No. of Options × Share Price × Conversion Ratio (if any).`}
                        _className="w-96"
                      >
                        <Icon
                          icon="material-symbols:info-outline-rounded"
                          height={18}
                          width={18}
                          className="w-full text-xs font-medium rounded cursor-pointer text-slate-dark items-center"
                        />
                      </Tooltip>
                    )}
                  </HStack>
                </span>
                <VStack className="w-2/3">
                  <span className="text-sm font-normal ">
                    {value?.toString()}
                  </span>
                  {error && <Error text={error} />}
                </VStack>
              </HStack>
            ))}
          </HStack>
        )}
      </VStack>
      <VStack className="flex-1 border rounded-lg shadow-box">
        <HStack
          className="items-center justify-between p-4 cursor-pointer"
          onClick={() =>
            setExpanded((prev) => ({
              ...prev,
              vestingSchedule: !prev.vestingSchedule,
            }))
          }
        >
          <VStack>
            <h5 className="text-sm font-medium text-black-501">
              Vesting Schedule
            </h5>
            {!expanded.vestingSchedule && (
              <HStack className="gap-2 pt-1 text-gray-400 text-xxs">
                {data.selectedExistingTemplate ? (
                  <span>
                    Template Name:{" "}
                    {data?.vestingTemplate?.vestingTemplateName || ""}
                  </span>
                ) : (
                  <span>
                    Template Name: {data?.manualTemplateName || "Manual"}
                  </span>
                )}
              </HStack>
            )}
          </VStack>
          <HStack className="items-center gap-8">
            {expanded.vestingSchedule && (
              <ButtonPrimary1
                onClick={(e) => {
                  e.stopPropagation();
                  props.onStepChange(3);
                }}
              >
                Edit
              </ButtonPrimary1>
            )}
            <Box>
              {expanded.vestingSchedule ? (
                <Icon icon="fe:arrow-up" width={16} />
              ) : (
                <Icon icon="fe:arrow-up" width={16} className="rotate-180" />
              )}
            </Box>
          </HStack>
        </HStack>
        <Dialog
          open={documentDialog.open}
          onClose={() => setDocumentDialog({ open: false })}
          maxWidth="lg"
        >
          <LazyPdfDocument url={documentDialog.grantLetter ?? ""} />
        </Dialog>
        {expanded.vestingSchedule && (
          <>
            <HStack className="flex-wrap grow">
              {[
                ...(data.selectedExistingTemplate
                  ? [
                      {
                        field: "Vesting Schedule",
                        value: data?.vestingTemplate?.vestingTemplateName || "",
                        error: errors.selectedVestingTemplate,
                      },
                      {
                        field: "Vesting Date Type",
                        value: formatToTitleCase(
                          (
                            data?.vestingDateType || VestingDateType.GRANT_DATE
                          ).replace("_", " ")
                        ),
                      },
                      {
                        field: "Actual Vesting Day",
                        value: formatToTitleCase(
                          (data?.actualVestingDay || "SAME_DAY").replace(
                            "_",
                            " "
                          )
                        ),
                      },
                      ...(data.vestingDateType &&
                      data.vestingDateType === VestingDateType.CUSTOM_DATE
                        ? [
                            {
                              field: "Vesting Start Date",
                              value: data?.vestingDate
                                ? formatDisplayDate(data?.vestingDate)
                                : "",
                            },
                          ]
                        : []),
                    ]
                  : [
                      {
                        field: "Template Name",
                        value: data?.manualTemplateName || "",
                      },
                      {
                        field: "Vesting Cliff (months)",
                        value: data?.manualVestingCliff?.toString() || "0",
                      },
                    ]),
              ].map(({ field, value, error }, i) => (
                <HStack key={i} className="w-1/2 gap-1 p-4">
                  <span className="w-1/3 text-sm font-normal text-gray-400">
                    {field}
                  </span>
                  <VStack className="w-2/3">
                    <span className="text-sm font-normal">{value}</span>
                    {error && <Error text={error} />}
                  </VStack>
                </HStack>
              ))}
            </HStack>
            {!data.selectedExistingTemplate && (
              <HStack className="pl-3">
                <table className="w-full table-space">
                  <thead className="text-xxs font-normal text-[#B8B7C9]">
                    <tr className="border-b border-gray-200 border-dashed">
                      <td className="px-2 py-2 align-top">Vesting Date</td>
                      <td className="px-2 py-2 align-top">No. Of options</td>
                      {data.vestingScheduleData &&
                        data.vestingScheduleData.some((v) => v.milestoneId) && (
                          <td className="px-2 py-2 align-top">
                            Milestone Name
                          </td>
                        )}
                    </tr>
                  </thead>
                  <tbody className="text-[#4E4E4E] ">
                    {data.vestingScheduleData.map((vesting, index) => (
                      <tr
                        key={index}
                        className="border-b border-gray-200 border-dashed "
                      >
                        <td className="px-2 py-2 align-top ">
                          <HStack>
                            <div className="mr-2 whitespace-nowrap">
                              {formatDisplayDate(vesting.vestingDate)}
                            </div>
                          </HStack>
                        </td>
                        <td className="px-2 py-2 align-top ">
                          <HStack>
                            <div className="mr-2 whitespace-nowrap">
                              {vesting.vestingOptions.toLocaleString(
                                currencyType,
                                {
                                  maximumFractionDigits: 4,
                                }
                              )}
                            </div>
                          </HStack>
                        </td>
                        {data.vestingScheduleData &&
                          data.vestingScheduleData.some(
                            (v) => v.milestoneId
                          ) && (
                            <td className="px-2 py-2 align-top ">
                              <HStack>
                                <div className="mr-2 whitespace-nowrap">
                                  {allMilestones.find(
                                    (m) => m.id === vesting.milestoneId
                                  )?.milestoneName || ""}
                                </div>
                              </HStack>
                            </td>
                          )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </HStack>
            )}
          </>
        )}
      </VStack>
      <HStack className="gap-8 px-2 pt-4">
        <div className="flex-1">
          <Label className="text-sm font-normal">Notes (Optional)</Label>
          <TextArea
            placeholder="Add any general notes about this grant."
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
          />
          {errors.note && <Error text={errors.note} />}
        </div>
        <div className="flex-1" />
      </HStack>
      <Box className="w-full max-w-4xl pt-4 pl-2">
        <HStack className="items-center gap-2 mb-4">
          <Label className="text-sm font-medium text-black-501">
            Generate Grant Letter
          </Label>
          <Tooltip
            id="grant-letter-generation-help"
            text="Turn this off to skip generation. You can optionally attach a completed PDF."
            _className="w-40 sm:w-48"
            align="right"
          >
            <Icon
              icon="material-symbols:info-outline-rounded"
              height={18}
              width={18}
              tabIndex={0}
              aria-label="About grant letter generation"
              aria-describedby="grant-letter-generation-help"
              className="rounded cursor-pointer text-slate-dark focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            />
          </Tooltip>
          <SwitchButton
            value={generateGrantLetter}
            label=""
            aria-label="Generate Grant Letter"
            onClick={() => data.setUploadCustomGrantLetter(generateGrantLetter)}
          />
        </HStack>
        {generateGrantLetter && (
          <>
            <HStack className="items-center gap-2 mb-2">
              <Label
                htmlFor="grant-letter-template-select"
                className="text-sm font-medium text-black-501"
              >
                Do you want to select a template?
              </Label>
              <Tooltip
                id="grant-letter-template-help"
                text="Select a saved grant template or upload a DOCX file for this grant. Optional. Leave both empty to use the configured template."
                _className="w-48 lg:w-96"
                align="right"
              >
                <Icon
                  icon="material-symbols:info-outline-rounded"
                  height={18}
                  width={18}
                  tabIndex={0}
                  aria-label="About grant letter templates"
                  aria-describedby="grant-letter-template-help"
                  className="rounded cursor-pointer text-slate-dark focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                />
              </Tooltip>
            </HStack>
            <HStack className="flex-col items-start gap-3 mb-6 sm:flex-row sm:items-center sm:gap-4">
              <div className="w-full sm:w-48 sm:shrink-0">
                <Select
                  id="grant-letter-template-select"
                  placeholder="Select a template"
                  value={
                    !data.grantLetterTemplate?.isAdded
                      ? data.grantLetterTemplate?.templateId || ""
                      : ""
                  }
                  options={grantLetterTemplates}
                  valueGetter={(o) => o.templateId || ""}
                  textGetter={(o) => o.planName || o.templateName || ""}
                  onChange={(e) => {
                    const template = grantLetterTemplates.find(
                      (t) => (t.templateId || "") === e.target.value
                    );
                    data.setGrantLetterTemplate(template || undefined);
                  }}
                />
              </div>
              <span className="text-sm font-medium text-gray-400">or</span>
              <HStack className="items-center gap-2 min-w-0">
                <FileInput
                  id="grant-letter-template"
                  accept=".docx"
                  file={
                    data.grantLetterTemplate?.isAdded
                      ? data.grantLetterTemplate.templateName ||
                        "Upload Template"
                      : "Upload Template"
                  }
                  onChange={handleTemplateFileUpload}
                  className="max-w-[200px] truncate"
                />
                <IconCTAButton
                  iconOnly
                  buttonType={IconButtonType.NONE}
                  aria-label="Remove grant letter template"
                  title="Remove grant letter template"
                  onClick={handleDeleteGrantLetterTemplate}
                  disabled={!hasGrantLetterTemplate}
                  className="shrink-0 text-secondaryText hover:text-primaryText focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:cursor-not-allowed disabled:text-gray-300 disabled:hover:text-gray-300"
                >
                  <DeleteIcon />
                </IconCTAButton>
              </HStack>
            </HStack>
            {grantSettings?.disableAutoGrantLetterGeneration && (
              <InfoBox message="Automatic grant letter generation is off in settings. Generate Grant Letter will still create one for this grant." />
            )}
          </>
        )}
        {!generateGrantLetter && (
          <VStack className="gap-2">
            <HStack className="gap-2 items-center pb-1">
              <Label className="text-sm font-medium text-black-501">
                Upload custom grant letter (optional)
              </Label>
              <Tooltip
                id="custom-grant-letter-help"
                text="Attach a completed PDF if you want to include a grant letter without generating one."
                _className="w-48 lg:w-96"
                align="right"
              >
                <Icon
                  icon="material-symbols:info-outline-rounded"
                  height={18}
                  width={18}
                  tabIndex={0}
                  aria-label="About custom grant letters"
                  aria-describedby="custom-grant-letter-help"
                  className="rounded cursor-pointer text-slate-dark focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                />
              </Tooltip>
            </HStack>
            <p className="text-xs text-secondaryText">
              Attach a completed PDF if you want to include a grant letter.
            </p>
            <HStack className="items-center gap-4 pl-2 min-w-0">
              {data.customizedGrantLetter.customizedLetter && mode !== "add" ? (
                <>
                  <div className="flex flex-row items-center justify-between cursor-pointer align-center">
                    {data.customizedGrantLetter.link ? (
                      <button
                        type="button"
                        className="text-red-500 underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                        onClick={() =>
                          setDocumentDialog({
                            open: true,
                            grantLetter: data.customizedGrantLetter.link,
                          })
                        }
                      >
                        Grant Letter.pdf
                      </button>
                    ) : (
                      <FileInput
                        id="custom-grant-letter-replace"
                        accept=".pdf"
                        file={data.customizedGrantLetter.fileName || "Replace"}
                        aria-describedby={
                          errors.customGrantLetter
                            ? "custom-grant-letter-error"
                            : undefined
                        }
                        onChange={(e) => {
                          handleFileUpload(e);
                        }}
                      />
                    )}
                  </div>
                </>
              ) : (
                <FileInput
                  id="custom-grant-letter"
                  accept=".pdf"
                  file={data.customizedGrantLetter?.fileName || "Attach File"}
                  aria-describedby={
                    errors.customGrantLetter
                      ? "custom-grant-letter-error"
                      : undefined
                  }
                  onChange={(e) => {
                    handleFileUpload(e);
                  }}
                />
              )}
              <IconCTAButton
                iconOnly
                buttonType={IconButtonType.NONE}
                aria-label="Remove custom grant letter"
                title="Remove custom grant letter"
                onClick={() => handleDeleteLetter()}
                disabled={!hasCustomGrantLetter}
                className="shrink-0 text-secondaryText hover:text-primaryText focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:cursor-not-allowed disabled:text-gray-300 disabled:hover:text-gray-300"
              >
                <DeleteIcon />
              </IconCTAButton>
            </HStack>
            {errors.customGrantLetter && (
              <p
                id="custom-grant-letter-error"
                role="alert"
                className="text-xs text-red-500"
              >
                {errors.customGrantLetter}
              </p>
            )}
          </VStack>
        )}
      </Box>
      {mode === "edit" && generateGrantLetter && (
        <div className="gap-6">
          <HStack className="w-full pt-4">
            <div className="flex gap-2">
              <div className="flex items-center w-18">
                <SwitchButton
                  value={replacingCustomLetter || regenerateGrantLetter}
                  label={""}
                  aria-label="Regenerate grant letter"
                  disabled={replacingCustomLetter}
                  onClick={() =>
                    setRegenerateGrantLetter(!regenerateGrantLetter)
                  }
                  className="mr-2"
                />
              </div>
              <div className="flex flex-col">
                <div className="font-medium">Regenerate Grant Letter</div>
                <div className="text-gray-500 text-xs">
                  {replacingCustomLetter
                    ? "A generated grant letter will replace the custom letter."
                    : "Toggle to regenerate the grant letter for the edited grant."}
                </div>
              </div>
            </div>
          </HStack>
          <HStack className="mt-2">
            {!shouldRegenerateGrantLetter && regenerateGrantLetter && (
              <InfoBox
                message={
                  "No material changes were detected in this grant. The grant letter will still be regenerated because the 'Regenerate Grant Letter' toggle is enabled. Disable the toggle if you do not want a new letter."
                }
              />
            )}

            {shouldRegenerateGrantLetter && !regenerateGrantLetter && (
              <InfoBox
                message={
                  "Changes were detected that would normally trigger a new grant letter, but regeneration is disabled. Enable the 'Regenerate Grant Letter' toggle to generate an updated letter."
                }
              />
            )}
          </HStack>
        </div>
      )}
      <HStack className="justify-between pt-4">
        <ButtonPrimary1 onClick={props.onBackClick}>Back</ButtonPrimary1>
        <ButtonPrimary
          onClick={() => handleSubmit()}
          loading={isLoading || editLoading}
        >
          Save Grant
        </ButtonPrimary>
      </HStack>
    </VStack>
  );
};

export default GrantSummaryPage;
